import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import AdminServices from '../../../Services/makeAdmin';
import { useLoaderData } from 'react-router-dom';
import './CommitteeComponent.css';

const CommitteeComponent = () => {
  const committeeList = useLoaderData();
  const [committees, setCommittees] = useState(committeeList.data);
  const [selectedCommitteeId, setSelectedCommitteeId] = useState(committees[0]?._id);

  const handleRemoveMember = async (committeeId, memberId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#008000',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Remove the member from the committee in the backend
          const res = await AdminServices.RemoveMemberFromCommittee(committeeId, memberId);

          if (res) {
            Swal.fire(
              'Deleted!',
              'Member removed successfully!',
              'success'
            );

            // Update state to reflect the removal
            const updatedCommittees = committees.map(committee => {
              if (committee._id === committeeId) {
                // Filter out the removed member
                const updatedMembers = committee.mainCommittee.members.filter(member => member._id !== memberId);
                return {
                  ...committee,
                  mainCommittee: {
                    ...committee.mainCommittee,
                    members: updatedMembers
                  }
                };
              } else {
                return committee;
              }
            });

            setCommittees(updatedCommittees);
          } else {
            Swal.fire({
              position: 'top-end',
              icon: 'error',
              title: 'Remove member failed',
              showConfirmButton: false,
              timer: 1500
            });
          }
        } catch (error) {
          // Show error message if removal fails
          Swal.fire({
            position: 'top-end',
            icon: 'error',
            title: 'Failed to remove member',
            showConfirmButton: false,
            timer: 1500
          });
        }
      }
    });
  };

  const addMemberToCommittee = async (committeeId, memberData) => {
    const formData = new URLSearchParams();
    for (const key in memberData) {
      formData.append(key, memberData[key]);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/committee/${committeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return await response.json();
    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
      throw error;
    }
  };

  const handleAddMember = async (committeeId) => {
    const { value: formValues } = await Swal.fire({
      title: 'Update Member',
      html:
        '<input id="swal-input1" class="swal2-input" placeholder="Name" required>' +
        '<input id="swal-input2" class="swal2-input" placeholder="Affiliation" required>' +
        '<input id="swal-input3" class="swal2-input" placeholder="Email" type="email" required>' +
        '<input id="swal-input4" class="swal2-input" placeholder="Designation" required>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update Member',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#5e71f6',
      cancelButtonColor: '#dc2626',
      preConfirm: () => {
        const name = document.getElementById('swal-input1').value;
        const affiliation = document.getElementById('swal-input2').value;
        const email = document.getElementById('swal-input3').value;
        const designation = document.getElementById('swal-input4').value;

        if (!name || !affiliation || !email || !designation) {
          Swal.showValidationMessage('All fields are required!');
          return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          Swal.showValidationMessage('Please enter a valid email address!');
          return false;
        }

        return {
          name,
          affiliation,
          email,
          designation,
        }
      }
    });
    if (formValues) {
      try {
        const res = await addMemberToCommittee(committeeId, formValues);

        if (res && res.mainCommittee && res.mainCommittee.members) {
          const newMember = res.mainCommittee.members[res.mainCommittee.members.length - 1]; // Assuming the new member is the last one in the array
          Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: 'Member updated successfully!',
            confirmButtonColor: '#5e71f6',
          });

          // Update the local state with the new member...
          setCommittees(committees.map(committee => {
            if (committee._id === committeeId) {
              return {
                ...committee,
                mainCommittee: {
                  ...committee.mainCommittee,
                  members: [...committee.mainCommittee.members, newMember],
                },
              };
            }
            return committee;
          }));
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to update member',
            confirmButtonColor: '#5e71f6',
          });
        }

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update member',
          confirmButtonColor: '#5e71f6',
        });
      }
    }
  };

  return (
    <div className="update-committee-wrapper">
      <div className="update-committee-container">
        {/* Header Section */}
        <div className="update-committee-header">
          <h1 className="update-committee-title">Update Committee</h1>
          <p className="update-committee-subtitle">Manage and update committee members</p>
        </div>

        {/* Committee Selection Card */}
        <div className="committee-selection-card">
          <div className="selection-card-header">
            <h2 className="selection-card-title">
              <svg className="selection-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Select Committee
            </h2>
          </div>

          <div className="selection-content">
            <select
              id="committee-select"
              className="committee-select-dropdown"
              onChange={(e) => setSelectedCommitteeId(e.target.value)}
              value={selectedCommitteeId}
            >
              {committees.map((committee) => (
                <option key={committee._id} value={committee._id}>
                  {committee.mainCommittee.committeeName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Committee Details Card */}
        {committees
          .filter((committee) => committee._id === selectedCommitteeId)
          .map((committee) => (
            <div className="committee-details-card" key={committee._id}>
              <div className="committee-details-header">
                <div className="committee-title-section">
                  <svg className="committee-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="committee-details-title">
                    {committee.mainCommittee.committeeName}
                  </h3>
                </div>
                <button 
                  className="update-member-button" 
                  onClick={() => handleAddMember(committee._id)}
                >
                  <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Update Member
                </button>
              </div>

              {/* Members List */}
              <div className="members-list-container">
                {committee.mainCommittee.members.length === 0 ? (
                  <div className="no-members-message">
                    <svg className="no-members-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p>No members found. Click "Add Member" to add new members.</p>
                  </div>
                ) : (
                  committee.mainCommittee.members.map((member, index) => (
                    <div className="member-card-item" key={member._id}>
                      <div className="member-card-header-row">
                        <div className="member-number-badge">
                          <svg className="member-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Member #{index + 1}
                        </div>
                        <button 
                          className="remove-member-button" 
                          onClick={() => handleRemoveMember(committee._id, member._id)}
                          title="Remove member"
                        >
                          <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Remove
                        </button>
                      </div>

                      <div className="member-info-grid">
                        <div className="member-info-item">
                          <span className="info-label">Name</span>
                          <span className="info-value">{member.name}</span>
                        </div>
                        <div className="member-info-item">
                          <span className="info-label">Affiliation</span>
                          <span className="info-value">{member.affiliation}</span>
                        </div>
                        <div className="member-info-item">
                          <span className="info-label">Email</span>
                          <span className="info-value">{member.email}</span>
                        </div>
                        <div className="member-info-item">
                          <span className="info-label">Designation</span>
                          <span className="info-value">{member.designation}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default CommitteeComponent;